import { z } from 'zod';

/** Persone, ruoli, presenze e membri dell'azienda (Fase 1). */

export const OPERATOR_CLASSES = ['knowledge_worker', 'linea_operativa'] as const;
export type OperatorClass = (typeof OPERATOR_CLASSES)[number];

const key = z.string().trim().regex(/^[a-z][a-z0-9_]{0,62}$/, 'usare minuscole, cifre e "_"');
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'data nel formato AAAA-MM-GG');

export const PersonSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  operator_class: z.enum(OPERATOR_CLASSES),
  user_id: z.string().uuid().nullable(),
  active: z.boolean(),
  roles: z.array(z.string()),
  open_tasks: z.number().int(),
  department: z.string().nullable(),
  manager_id: z.string().uuid().nullable(),
  /** `manuale` o `archivio:<tipo>` se arriva da un'importazione. */
  source: z.string(),
  /** Aree della pagina Procedure di cui è responsabile: con il suo account le verifica, cambia e aggiunge. */
  procedure_areas: z.array(z.string()),
  /** Sedi in cui lavora (ADR 0017): tutte, oppure quelle elencate con i ruoli che vi svolge (vuoto = tutti i suoi). */
  all_sites: z.boolean(),
  sites: z.array(z.object({ site_id: z.string().uuid(), roles: z.array(z.string()) })),
});
export type Person = z.infer<typeof PersonSchema>;

export const CreatePersonSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email().max(254).optional(),
  phone: z.string().trim().max(40).optional(),
  operator_class: z.enum(OPERATOR_CLASSES).default('linea_operativa'),
  roles: z.array(key).default([]),
  /** Collega la persona all'utente che la crea (per vedere i propri task). */
  link_me: z.boolean().optional(),
});
export type CreatePersonInput = z.infer<typeof CreatePersonSchema>;

export const UpdatePersonSchema = CreatePersonSchema.partial().extend({
  active: z.boolean().optional(),
  /** Responsabile delle procedure di queste aree (vuoto = nessuna). Serve un account: email di un utente registrato. */
  procedure_areas: z.array(z.string().trim().min(1).max(120)).max(50).optional(),
});
export type UpdatePersonInput = z.infer<typeof UpdatePersonSchema>;

export const RoleSchema = z.object({ key: z.string(), label: z.string(), people: z.number().int() });
export type Role = z.infer<typeof RoleSchema>;
export const CreateRoleSchema = z.object({ key, label: z.string().trim().min(1).max(80) });

export const PresenceDaySchema = z.object({
  day,
  data: z.array(z.object({ person_id: z.string().uuid(), name: z.string(), roles: z.array(z.string()), present: z.boolean().nullable() })),
});
export type PresenceDay = z.infer<typeof PresenceDaySchema>;

export const SetPresenceSchema = z.object({
  day,
  entries: z.array(z.object({ person_id: z.string().uuid(), present: z.boolean() })).min(1),
});
export type SetPresenceInput = z.infer<typeof SetPresenceSchema>;

export const AddMemberSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: z.enum(['admin', 'member']).default('member'),
});
export type AddMemberInput = z.infer<typeof AddMemberSchema>;

/** Importazione di persone e ruoli da dati dell'archivio (programma collegato o export dell'organigramma). */
const attr = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);
export const PeopleImportConfigSchema = z.object({
  tipo: attr,
  nome: attr,
  /** Se nome e cognome sono in due attributi. */
  cognome: attr.nullable().default(null),
  email: attr.nullable().default(null),
  telefono: attr.nullable().default(null),
  /** Attributi i cui valori diventano ruoli (es. mansione, reparto). */
  ruoli: z.array(attr).max(3).default([]),
  reparto: attr.nullable().default(null),
  /** Attributo con il responsabile: riconosciuto per codice, email o nome tra le persone importate. */
  responsabile: attr.nullable().default(null),
  /** Attributo che dice se la persona è attiva; vuoto = tutte attive. */
  attivo: attr.nullable().default(null),
  /** Chi non è più nei dati viene disattivato (mai cancellato). */
  disattiva_assenti: z.boolean().default(true),
});
export type PeopleImportConfig = z.infer<typeof PeopleImportConfigSchema>;

export interface PeopleImportPreview {
  righe: number;
  nuove: number;
  aggiornate: number;
  disattivate: number;
  ruoli_nuovi: string[];
  responsabili_non_trovati: number;
  campione: Array<{ nome: string; email: string | null; ruoli: string[]; reparto: string | null; responsabile: string | null; esito: 'nuova' | 'aggiornata' | 'invariata' }>;
  avvisi: string[];
}

export interface PeopleImportState {
  config: PeopleImportConfig | null;
  last_run_at: string | null;
  last_result: PeopleImportPreview | null;
  /** Proposta di abbinamento per ogni tipo dell'archivio che sembra un elenco di persone. */
  proposte: Array<{ tipo: string; etichetta: string; entita: number; fonte: string; config: PeopleImportConfig; punteggio: number }>;
}
